import { EmailLinkError } from '@components/EmailLinkError'
import LoadingAnimation from '@components/LoadingAnimation'
import { getEnvPrefix } from '@lib/config'
import {
  isSignInWithEmailLink,
  onAuthStateChanged,
  signInWithEmailLink,
} from 'firebase/auth'
import mixpanel from 'mixpanel-browser'
import { useEffect, useState } from 'react'
import { AuthContext, AuthedUser } from '../hooks/useAuth'
import {
  auth,
  getPrivateUserPath,
  getPublicConciergePath,
} from '../lib/firebase'
import { getFirestore } from 'firebase/firestore'
import Login from '../pages/Login'
import ThemeCustomization from '../themes'
import { collection, getDocs, query, where } from 'firebase/firestore'

const ERROR_MESSAGES = {
  INVALID_LINK:
    'This sign-in link is invalid or has expired. Please try again.',
  RESEND_SUCCESS:
    'Link expired. No worries, we sent a new sign-in link to your email!',
  RESEND_FAILURE: "Sorry! Sign in didn't work. Please try again.",
}

const CONCIERGES_COLLECTION = 'concierges'

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthedUser | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [processingEmailLink, setProcessingEmailLink] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [emailAutosentTo, setemailAutosentTo] = useState<string>('')
  const [privateStoragePath, setPrivateStoragePath] = useState<string | null>(
    null,
  )
  const [publicStoragePath, setPublicStoragePath] = useState<string | null>(
    null,
  )
  const isEmailLink = isSignInWithEmailLink(auth, window.location.href)

  function closeNotification(): void {
    setShowError(false)
  }

  function extractEmailFromParams(): string | null {
    const params = new URLSearchParams(window.location.search)
    const userParam = params.get('user')

    if (userParam) {
      try {
        return atob(userParam)
      } catch (error) {
        console.error('Error retrieving email parameter.', error)
        return null
      }
    }

    return null
  }

  function handleEmailLinkError(
    message: string,
    resent = false,
    email?: string,
  ) {
    setErrorMessage(message)
    if (email && resent) {
      setemailAutosentTo(email)
    }
    setShowError(true)
    setProcessingEmailLink(false)
  }

  async function attemptEmailLinkSignIn(email: string): Promise<boolean> {
    try {
      await signInWithEmailLink(auth, email, window.location.href)
      return true
    } catch (error) {
      console.error('Error signing in with email link:', error)
      return false
    }
  }

  useEffect(() => {
    async function processEmailLink() {
      if (!isEmailLink) return

      setProcessingEmailLink(true)
      const email = extractEmailFromParams()

      if (!email) {
        handleEmailLinkError(ERROR_MESSAGES.INVALID_LINK)
        return
      }

      const success = await attemptEmailLinkSignIn(email)
      if (!success) {
        await handleEmailLinkFailure(email)
      } else {
        setProcessingEmailLink(false)
      }
    }

    async function handleEmailLinkFailure(email: string) {
      handleEmailLinkError(ERROR_MESSAGES.RESEND_FAILURE, false, email)
    }

    processEmailLink()
  }, [isEmailLink])

  async function fetchStorageKeys(authedUser: AuthedUser) {
    const apiUrl = `https://${getEnvPrefix()}api.digitalwisdom.ai/keys/user`
    try {
      const token = await authedUser.getIdToken()
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          'dw-uid': authedUser.uid,
        },
      })
      if (response.ok) {
        const data = await response.json()
        if (data.keys) {
          setPrivateStoragePath(
            data.keys.storage_private
              ? getPrivateUserPath(data.keys.storage_private)
              : null,
          )
          setPublicStoragePath(
            data.keys.storage_public
              ? getPublicConciergePath(data.keys.storage_public)
              : null,
          )
        }
      }
    } catch (error) {
      console.error(`Error fetching user hashes from ${apiUrl}:`, error)
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUser(null)
        setIsAdmin(false)
        setPrivateStoragePath(null)
        setPublicStoragePath(null)
        setLoading(false)
        return
      }

      const authedUser = user as AuthedUser
      setUser(authedUser)

      try {
        const tokenResult = await authedUser.getIdTokenResult()
        setIsAdmin(tokenResult.claims?.admin === true)
        await fetchStorageKeys(authedUser)
      } catch (error) {
        console.error('Error fetching admin status:', error)
        setIsAdmin(false)
      } finally {
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (!user?.email) return

    const userEmail = user.email

    // Fetch concierge data for the user's email
    async function fetchConciergeForAlias() {
      const db = getFirestore()
      const q = query(
        collection(db, CONCIERGES_COLLECTION),
        where('email', '==', userEmail),
      )

      try {
        const snapshot = await getDocs(q)

        // TO DO: handle multiple concierges for the same email
        if (!snapshot.empty && snapshot.docs.length == 1) {
          const concierge = snapshot.docs[0]
          console.log('Setting mixpanel identity for concierge', concierge.id)
          mixpanel.identify(concierge.id)
          mixpanel.people.set({
            $email: userEmail,
          })
        } else if (snapshot.empty) {
          console.log('No concierge found for email:', userEmail)
        }
      } catch (error) {
        console.error('Error fetching concierge for alias:', error)
      }
    }

    fetchConciergeForAlias()
  }, [user])

  if (loading || processingEmailLink) {
    return <LoadingAnimation />
  }

  if (user === null && window.location.pathname === '/wix-install') {
    window.location.href = 'https://try.webwisdom.ai'
    return null
  }

  if (user === null || !user.email) {
    return (
      <ThemeCustomization>
        <Login emailAutosentTo={emailAutosentTo} />
        <EmailLinkError
          open={showError}
          onClose={closeNotification}
          errorMessage={errorMessage}
          emailAutosent={!!emailAutosentTo}
        />
      </ThemeCustomization>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        privateStoragePath,
        publicStoragePath,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
